import { $logger } from '@/boot/logger'
import { $pegApi } from '@/boot/peg-api'

/**
 * @typedef {Object} Box
 * @property {number|null} length - Box length in inches
 * @property {number|null} width - Box width in inches
 * @property {number|null} height - Box height in inches
 * @property {number|null} weight - Box weight in pounds
 * @property {string|null} dimensionsPhotoId - Media ID of uploaded dimensions photo (optional)
 * @property {string|null} dimensionsPhotoThumbnail - Thumbnail URL of dimensions photo (optional)
 * @property {number|null} dimensionsConfidence - Confidence score for dimensions (optional)
 */

/**
 * @typedef {Object} ProductPackagingAssessment
 * @property {'not-applicable'|'original-sealed'|'original-opened'|'own-box'|null} packagingType
 * @property {'like-new'|'good'|'fair'|'poor'|null} boxCondition
 * @property {'all'|'some'|'none'|'not-applicable'|null} internalMaterials
 * @property {Box[]} boxes - Array of box objects with dimensions, weight, and photo IDs
 * @property {string} dimensionUnit - Unit of measurement, defaults to 'in'
 * @property {string} weightUnit - Unit of measurement, defaults to 'lb'
 */

/**
 * @typedef {Object} ItemPackagingAssessmentState
 * @property {ProductPackagingAssessment} assessment - Assessment data for this item
 * @property {number} currentStep - Current step in assessment flow
 * @property {boolean} isComplete - Whether assessment is complete
 */

/**
 * Customer Return Packaging State Store
 *
 * Manages per-item packaging assessments during the customer return flow.
 * This store handles:
 * - Per-item packaging assessment tracking (keyed by lineItemSessionId)
 * - Packaging type identification (original/own box/none)
 * - Box condition assessment
 * - Internal materials tracking
 * - Dimension measurements for custom packaging
 * - Multi-step form navigation
 *
 * @public API:
 * - State: productPackagingAssessment (computed), currentStep (computed), totalSteps (computed), activeItemSessionId
 * - Actions: setActiveItem, initializeItemAssessment, getAssessmentForItem, isItemAssessmentComplete, nextStep, prevStep, reset, submitForm, getPackagingPhotoIds
 *
 * @store customerReturnPackagingState
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Default box object structure
 * @public - Can be used by components for initializing new boxes
 */
export const DEFAULT_BOX = {
  length: null,
  width: null,
  height: null,
  weight: null,
  dimensionsPhotoId: null,
  dimensionsPhotoThumbnail: null,
  dimensionsConfidence: null,
}

/** @type {ProductPackagingAssessment} */
const DEFAULT_PRODUCT_PACKAGING_ASSESSMENT = {
  packagingType: null,
  boxCondition: null,
  internalMaterials: null,
  boxes: [],
  dimensionUnit: 'in',
  weightUnit: 'lb',
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnPackagingStateStore = defineStore('customerReturnPackagingState', () => {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Per-item packaging assessments keyed by lineItemSessionId
   * @type {Ref<Record<string, ItemPackagingAssessmentState>>}
   * @private
   */
  const packagingAssessments = ref({})

  /**
   * Currently active item's lineItemSessionId
   * @type {Ref<string|null>}
   * @public
   */
  const activeItemSessionId = ref(null)

  /**
   * Set of item IDs that have had prepopulation attempted (once per item)
   * @type {Ref<Set<string>>}
   * @private
   */
  const prepopulatedItemIds = ref(new Set())

  /**
   * PEG-822 (AC5): set of item IDs for which the carrier-gap telemetry event has
   * already been emitted. This is a DEDICATED latch — deliberately separate from
   * prepopulatedItemIds, which StepBoxDimensions clears on every mount
   * (clearPrepopulationForItem) so it can re-attempt prepopulation. Sharing that
   * flag for the AC5 once-per-item guard caused two defects (Codex/Copilot review,
   * PR #480): (a) on a Back/forward revisit the cleared prepopulation flag let the
   * gap event re-emit, violating "once per item"; (b) when own-box first visited
   * the rendered step it marked prepopulation, so a later original-* path with no
   * carrier dims wrongly found the item "already logged" and SUPPRESSED a genuine
   * first gap event. This latch is never cleared by StepBoxDimensions.
   * @type {Ref<Set<string>>}
   * @private
   */
  const carrierGapLoggedItemIds = ref(new Set())

  /**
   * Product packaging assessment data for active item (computed)
   * @type {ComputedRef<ProductPackagingAssessment>}
   * @public
   */
  const productPackagingAssessment = computed(() => {
    if (!activeItemSessionId.value) {
      return { ...DEFAULT_PRODUCT_PACKAGING_ASSESSMENT }
    }
    return packagingAssessments.value[activeItemSessionId.value]?.assessment || { ...DEFAULT_PRODUCT_PACKAGING_ASSESSMENT }
  })

  /**
   * Current step in the assessment flow for active item (computed)
   * @type {ComputedRef<number>}
   * @public
   */
  const currentStep = computed(() => {
    if (!activeItemSessionId.value) return 1
    return packagingAssessments.value[activeItemSessionId.value]?.currentStep || 1
  })

  /**
   * Total number of steps in the assessment (dynamic based on packaging type)
   * @type {ComputedRef<number>}
   * @public
   */
  const totalSteps = computed(() => {
    const type = productPackagingAssessment.value.packagingType
    if (type === 'not-applicable') return 1 // type only
    if (type === 'original-sealed') return 3 // type + condition + boxes
    if (type === 'original-opened') return 4 // type + condition + materials + boxes
    if (type === 'own-box') return 3 // type + materials + boxes
    return 3 // default
  })

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Set the active item for assessment
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function setActiveItem(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Packaging state: Cannot set active item without lineItemSessionId')
      return
    }

    activeItemSessionId.value = lineItemSessionId

    // Initialize if doesn't exist
    if (!packagingAssessments.value[lineItemSessionId]) {
      initializeItemAssessment(lineItemSessionId)
    }
  }

  /**
   * Initialize assessment for a specific item
   * @param {string} lineItemSessionId - Line item session ID
   * @public
   */
  function initializeItemAssessment(lineItemSessionId) {
    if (!lineItemSessionId) {
      $logger.warn('Packaging state: Cannot initialize without lineItemSessionId')
      return
    }

    // Don't reinitialize if already exists
    if (packagingAssessments.value[lineItemSessionId]) {
      return
    }

    packagingAssessments.value[lineItemSessionId] = {
      assessment: { ...DEFAULT_PRODUCT_PACKAGING_ASSESSMENT },
      currentStep: 1,
      isComplete: false,
    }
  }

  /**
   * Get assessment data for a specific item
   * @param {string} lineItemSessionId - Line item session ID
   * @returns {ItemPackagingAssessmentState|null}
   * @public
   */
  function getAssessmentForItem(lineItemSessionId) {
    if (!lineItemSessionId) return null
    return packagingAssessments.value[lineItemSessionId] || null
  }

  /**
   * Check if item's assessment is complete
   * @param {string} lineItemSessionId - Line item session ID
   * @returns {boolean}
   * @public
   */
  function isItemAssessmentComplete(lineItemSessionId) {
    if (!lineItemSessionId) return false
    return packagingAssessments.value[lineItemSessionId]?.isComplete || false
  }

  /**
   * Navigate to next step based on packaging assessment answers
   * Logic: Skip steps based on packaging type
   * Flow:
   * - not-applicable: Step 1 (type) - final step
   * - original-sealed: Step 1 (type) → Step 2 (condition) → Step 3 (boxes) - final step
   * - original-opened: Step 1 (type) → Step 2 (condition) → Step 3 (materials) → Step 4 (boxes) - final step
   * - own-box: Step 1 (type) → Step 2 (materials) → Step 3 (boxes) - final step
   * @public
   */
  function nextStep() {
    if (!activeItemSessionId.value) {
      $logger.warn('Packaging state: Cannot navigate without active item')
      return
    }

    const itemState = packagingAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.error('Packaging state: No state found for active item', { activeItemSessionId: activeItemSessionId.value })
      return
    }

    const type = itemState.assessment.packagingType
    const step = itemState.currentStep

    if (step === 1) {
      // From packaging type selection
      if (type === 'not-applicable') {
        // This is the final step - no navigation needed
        return
      } else {
        itemState.currentStep = 2 // Go to condition or materials
      }
    } else if (step === 2) {
      // From condition (original-sealed/opened) or materials (own-box)
      if (type === 'original-sealed') {
        itemState.currentStep = 3 // Go to boxes (final step)
      } else if (type === 'original-opened') {
        itemState.currentStep = 3 // Go to materials
      } else if (type === 'own-box') {
        itemState.currentStep = 3 // Go to boxes (final step)
      }
    } else if (step === 3) {
      // From materials (original-opened) or boxes (sealed/own-box)
      if (type === 'original-opened') {
        itemState.currentStep = 4 // Go to boxes (final step)
      }
      // For sealed/own-box, this is the final step - no navigation needed
    }
    // Step 4 is only for original-opened and is the final step
  }

  /**
   * Navigate to previous step based on packaging assessment history
   * Logic: Determine previous step based on packaging type and current step
   * @public
   */
  function prevStep() {
    if (!activeItemSessionId.value) {
      $logger.warn('Packaging state: Cannot navigate without active item')
      return
    }

    const itemState = packagingAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.error('Packaging state: No state found for active item', { activeItemSessionId: activeItemSessionId.value })
      return
    }

    const step = itemState.currentStep
    const type = itemState.assessment.packagingType

    if (step === 2) {
      // Reset step 2 answer: boxCondition (original-sealed/opened) or internalMaterials (own-box)
      if (type === 'own-box') {
        itemState.assessment.internalMaterials = null
      } else {
        itemState.assessment.boxCondition = null
      }
      itemState.currentStep = 1
    } else if (step === 3) {
      // Reset step 3 answer: internalMaterials (original-opened) or boxes (sealed/own-box)
      if (type === 'original-opened') {
        itemState.assessment.internalMaterials = null
      } else {
        deleteAllSavedBoxes(activeItemSessionId.value)
        itemState.assessment.boxes = []
      }
      itemState.currentStep = 2
    } else if (step === 4) {
      // Only original-opened reaches step 4 (boxes)
      deleteAllSavedBoxes(activeItemSessionId.value)
      itemState.assessment.boxes = []
      itemState.currentStep = 3 // Back to materials
    }
  }

  /**
   * Delete all saved boxes from the API for a given return session item.
   * Fire-and-forget: errors are logged but do not block navigation.
   * @param {string} returnSessionItemId - Return session item ID
   * @private
   */
  function deleteAllSavedBoxes(returnSessionItemId) {
    if (!returnSessionItemId) return

    $pegApi.ReturnSessionItemsBoxes.getBoxes(returnSessionItemId)
      .then(response => {
        const boxes = response?.boxes || []
        if (boxes.length === 0) return

        return Promise.all(
          boxes.map(box =>
            $pegApi.ReturnSessionItemsBoxes.deleteBox(returnSessionItemId, box.id).catch(err => {
              $logger.error('Packaging state: Failed to delete box', { boxId: box.id, error: err })
            })
          )
        )
      })
      .catch(err => {
        $logger.error('Packaging state: Failed to fetch boxes for deletion', {
          returnSessionItemId,
          error: err,
        })
      })
  }

  /**
   * Set the current step directly (used for browser back/forward sync)
   * @param {number} step - Step number to set
   * @public
   */
  function setCurrentStep(step) {
    if (!activeItemSessionId.value) {
      $logger.warn('Packaging state: Cannot set step without active item')
      return
    }

    const itemState = packagingAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.warn('Packaging state: No state found for active item')
      return
    }

    const oldStep = itemState.currentStep
    const type = itemState.assessment.packagingType

    // When navigating backwards, reset the answer(s) for the step(s) being left
    if (step < oldStep) {
      // Reset boxes if leaving a boxes step
      const boxesStep = type === 'original-opened' ? 4 : 3
      if (oldStep >= boxesStep && step < boxesStep) {
        deleteAllSavedBoxes(activeItemSessionId.value)
        itemState.assessment.boxes = []
      }
      // Reset internalMaterials if leaving a materials step
      const materialsStep = type === 'original-opened' ? 3 : type === 'own-box' ? 2 : null
      if (materialsStep && oldStep >= materialsStep && step < materialsStep) {
        itemState.assessment.internalMaterials = null
      }
      // Reset boxCondition if leaving step 2 for original-sealed/opened
      if (oldStep >= 2 && step < 2 && (type === 'original-sealed' || type === 'original-opened')) {
        itemState.assessment.boxCondition = null
      }
    }

    itemState.currentStep = step
  }

  /**
   * Reset assessment to initial state
   * @param {string} [lineItemSessionId] - Optional: Reset specific item, or all if omitted
   * @public
   */
  function reset(lineItemSessionId) {
    if (lineItemSessionId) {
      // Reset specific item
      if (packagingAssessments.value[lineItemSessionId]) {
        packagingAssessments.value[lineItemSessionId] = {
          assessment: { ...DEFAULT_PRODUCT_PACKAGING_ASSESSMENT },
          currentStep: 1,
          isComplete: false,
        }
      }
    } else {
      // Reset all
      packagingAssessments.value = {}
      activeItemSessionId.value = null
      prepopulatedItemIds.value = new Set()
      carrierGapLoggedItemIds.value = new Set()
    }
  }

  /**
   * Get all packaging photo IDs from boxes array for active item
   * @returns {string[]} Array of unique photo IDs
   * @public
   */
  function getPackagingPhotoIds() {
    const assessment = productPackagingAssessment.value
    const photoIds = []
    if (assessment.boxes && Array.isArray(assessment.boxes)) {
      assessment.boxes.forEach(box => {
        if (box.dimensionsPhotoId) photoIds.push(box.dimensionsPhotoId)
      })
    }
    return [...new Set(photoIds)] // Remove duplicates
  }

  /**
   * Submit the packaging assessment form and build formatted data
   * Saves to active item's state
   * @param {ProductPackagingAssessment} answers - Assessment answers
   * @returns {Object} Formatted submission data
   * @public
   */
  function submitForm(answers) {
    if (!activeItemSessionId.value) {
      $logger.error('Packaging state: Cannot submit without active item')
      return null
    }

    const itemState = packagingAssessments.value[activeItemSessionId.value]
    if (!itemState) {
      $logger.error('Packaging state: No state found for active item', { activeItemSessionId: activeItemSessionId.value })
      return null
    }

    // Update the item's assessment with submitted answers
    itemState.assessment = { ...answers }
    itemState.isComplete = true

    const formData = {
      packagingType: answers.packagingType,
      isSealed: answers.packagingType === 'original-sealed',
      hasOriginalPackaging: ['not-applicable', 'original-opened', 'original-sealed'].includes(answers.packagingType),
      ...(['original-opened', 'original-sealed'].includes(answers.packagingType) && { boxCondition: answers.boxCondition }),
      ...(answers.packagingType === 'original-opened' && { internalMaterials: answers.internalMaterials }),
      ...(answers.packagingType === 'original-sealed' && { internalMaterials: 'all' }),
      ...(answers.packagingType === 'own-box' && { internalMaterials: answers.internalMaterials }),
    }

    // Include boxes array for all types except 'not-applicable'
    if (answers.packagingType !== 'not-applicable') {
      formData.boxes = answers.boxes || []
      formData.dimensionUnit = answers.dimensionUnit
      formData.weightUnit = answers.weightUnit
      formData.packagingPhotoIds = getPackagingPhotoIds()
    }

    return formData
  }

  /**
   * Mark an item as having had prepopulation attempted
   * @param {string} returnSessionItemId - Return session item ID
   * @public
   */
  function markItemPrepopulated(returnSessionItemId) {
    if (!returnSessionItemId) {
      $logger.warn('Packaging state: Cannot mark prepopulated without returnSessionItemId')
      return
    }
    prepopulatedItemIds.value.add(returnSessionItemId)
  }

  /**
   * Check if an item has had prepopulation attempted
   * @param {string} returnSessionItemId - Return session item ID
   * @returns {boolean}
   * @public
   */
  function hasItemBeenPrepopulated(returnSessionItemId) {
    if (!returnSessionItemId) return false
    return prepopulatedItemIds.value.has(returnSessionItemId)
  }

  /**
   * Clear prepopulation tracking for a specific item
   * NOTE: Parent component must call this during item transition to reset prepopulation tracking for the next item
   * @param {string} returnSessionItemId - Return session item ID
   * @public
   */
  function clearPrepopulationForItem(returnSessionItemId) {
    if (!returnSessionItemId) {
      $logger.warn('Packaging state: Cannot clear prepopulation without returnSessionItemId')
      return
    }
    prepopulatedItemIds.value.delete(returnSessionItemId)
  }

  /**
   * PEG-822 (AC5): mark that the carrier-gap telemetry event has been emitted for
   * this item. Dedicated latch, NOT cleared by StepBoxDimensions — guarantees the
   * gap event fires at most once per item across Back/forward revisits and across
   * mixed packaging-type paths. (Codex/Copilot review, PR #480.)
   * @param {string} returnSessionItemId - Return session item ID
   * @public
   */
  function markCarrierGapLogged(returnSessionItemId) {
    if (!returnSessionItemId) {
      $logger.warn('Packaging state: Cannot mark carrier-gap logged without returnSessionItemId')
      return
    }
    carrierGapLoggedItemIds.value.add(returnSessionItemId)
  }

  /**
   * PEG-822 (AC5): whether the carrier-gap telemetry event has already been
   * emitted for this item. (Codex/Copilot review, PR #480.)
   * @param {string} returnSessionItemId - Return session item ID
   * @returns {boolean}
   * @public
   */
  function hasCarrierGapBeenLogged(returnSessionItemId) {
    if (!returnSessionItemId) return false
    return carrierGapLoggedItemIds.value.has(returnSessionItemId)
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // State (computed for active item)
    currentStep,
    totalSteps,
    productPackagingAssessment,
    activeItemSessionId,
    packagingAssessments, // Exposed for direct component access
    // Actions
    setActiveItem,
    initializeItemAssessment,
    getAssessmentForItem,
    isItemAssessmentComplete,
    reset,
    nextStep,
    prevStep,
    setCurrentStep,
    submitForm,
    getPackagingPhotoIds,
    markItemPrepopulated,
    hasItemBeenPrepopulated,
    clearPrepopulationForItem,
    markCarrierGapLogged,
    hasCarrierGapBeenLogged,
  }
})
